import React, { Component, createRef } from 'react';
import { Root } from '../../components/ui';
import { TextSearchField } from '../../components/ui/form';
import { ButtonContainer, SecondaryButton } from '../../ui';
import { TableToolBar } from '../../components/ui/table';
import UITable from '../../components/UITable';
import _, { find } from 'lodash';
import { TableSpec } from './tableSpec';
import { compose, withState } from 'react-recompose';
import { messages } from '../../i18n';
import {
  IVizImport,
  IVizExport,
  IVizReportEditing,
  IDehydratedViz,
  IViz,
  IDehydratedVizOption,
} from '../../discovery/interfaces';
import { IDataset } from '../../datasets/interfaces';
import { SearchOptionGroup } from './library.styles';
import { HelpBlock } from '../../views/VizSaveDialog/ui';
import { FilterWidget } from '../../discovery/filter-widget/filter-widget.component';
import { FilterWidgetContextProvider } from '../../discovery/filter-widget/filter-widget.context';
import { ToastHOC } from '../../components/toast/toast-launcher';
import { EditDiscoveryDialog } from '../edit-discovery-dialog';
import { withErrorBoundary } from 'react-error-boundary';
import { ErrorBoundaryHOCOptions } from '../../components/error-boundary';
import { TableSkeleton } from '../../common/loaders';
import {
  CreateReportButton,
  MainSectionHeader,
} from '../../components/main-section-header';
import ImportReportsDuplicateDialog from '../../common/widgets/dialogs/ImportReportsDuplicateDialog';
import { LIBRARY_FILTER_HEADER_WIDTH, VIZ_OPTION_IDS } from '../../common';
import { PromptDialog } from '../../common/widgets/dialogs/prompt-dialog/prompt-dialog.component';
import { defaultCellDataGetter } from '../../datasets/utils';
import { Viz } from '../../discovery/VizUtil';
import { sanitizeLibraryContent } from './library-utils';

interface IProps {
  visualizations: (IDehydratedViz & { description: string })[];
  reportDownloadObjectUrl: any;
}

interface IState {
  updatingViz: any;
  showEdit: any;
  showDelete: any;
  showImportDuplicates: any;
  editKey: any;
  tenantName: any;
  newReports: any;
  reportsRequiringInput: any;
  reportsNotRequiringInput: any;
}

class LibraryComponentUnconnected extends Component<IProps, IState> {
  state;
  props;
  hiddenFileInput;

  static defaultProps = {
    content: [],
  };

  constructor(props) {
    super(props);

    this.hiddenFileInput = createRef();

    this.state = {
      updatingViz: null,
      showEdit: false,
      showDelete: false,
      showImportDuplicates: false,
      editKey: 'name',
      tenantName: props?.currentUser?.tenant || 'tenant',
    };
  }

  onSearch(search) {
    this.props.setSearch(search);
  }

  onSetTags(tags: string[]) {
    this.props.setSearchTags(tags);
  }

  onSetSearchChartType(chartTypes: string[]) {
    this.props.setSearchChartTypes(chartTypes);
  }

  onSetDatasets(datasets: string[]) {
    this.props.setSearchDatasets(datasets);
  }

  doAction(eventKey, viz: IViz) {
    switch (eventKey) {
      case 'name':
      case 'description':
        this.showEditPrompt(viz, eventKey);
        break;
      case 'delete':
        this.showDeletePrompt(viz);
        break;
      case 'public':
        this.updatePrivacy(viz, false);
        break;
      case 'private':
        this.updatePrivacy(viz, true);
        break;
    }
  }

  updatePrivacy(viz, isPrivate) {
    this.props.updateVisualization(
      { ...viz, private: isPrivate },
      'isPrivate',
      {
        onError: (error: Error) => {
          this.props.showToast({
            label: messages.table.privacySaveError,
            type: 'error',
            children: <span>{error?.message}</span>,
          });
        },
      },
    );
  }

  showDeletePrompt(viz) {
    this.setState({ updatingViz: viz, showDelete: true });
  }

  getDeleteConfirmation() {
    const name = this.state?.updatingViz?.name ?? '';
    return (
      <div className='promptMessage'>
        <HelpBlock>
          {messages.formatString(messages.library.deleteConfirmMessage, {
            name: <strong>{name}</strong>,
            type: 'report',
          })}
        </HelpBlock>
      </div>
    );
  }

  doDeleteYes() {
    if (this.state.updatingViz?.id) {
      this.props.deleteVisualization(this.state.updatingViz?.id);
    }
    this.setState({ updatingViz: null, showDelete: false });
  }

  doDeleteNo() {
    this.setState({ updatingViz: null, showDelete: false });
  }

  showEditPrompt(viz, eventKey) {
    this.setState({
      updatingViz: viz,
      showEdit: true,
      editKey: eventKey,
    });
  }

  doEditYes(value) {
    const { updatingViz } = this.state;
    const { editKey } = this.state;
    if (updatingViz?.id) {
      switch (editKey) {
        case 'name':
          this.props.updateVisualization(
            { ...updatingViz, name: value },
            editKey,
            {
              onError: (error: Error) => {
                this.props.showToast({
                  label: messages.calcDialog.nameAlreadyExists,
                  type: 'error',
                  children: <span>{error?.message}</span>,
                });
              },
            },
          );
          break;
        case 'description': {
          let currentDescription: IDehydratedVizOption = find(
            updatingViz?.options as IDehydratedVizOption[],
            {
              key: VIZ_OPTION_IDS.description,
            },
          );

          if (!currentDescription) {
            currentDescription = {
              key: VIZ_OPTION_IDS.description,
              value,
            };
          }

          currentDescription.value = currentDescription;

          const options = [
            ...((updatingViz?.options as IDehydratedVizOption[]) ?? []),
            currentDescription,
          ];
          this.props.updateVisualization({ ...updatingViz, options }, editKey, {
            onError: (error: Error) => {
              this.props.showToast({
                label: messages.table.descriptionSaveError,
                type: 'error',
                children: <span>{error?.message}</span>,
              });
            },
          });
          break;
        }
      }
    }
    this.setState({ updatingViz: null, showEdit: false });
  }

  doEditNo() {
    this.setState({ updatingViz: null, showEdit: false });
  }

  onRowClick({ rowData: dehydratedViz }: { rowData: IDehydratedViz }) {
    if (this.props.actionsOpen) {
      return;
    }
    this.props.openVisualization(Viz.rehydrateViz(dehydratedViz));
  }

  genReportDownloadBlob(filteredContent?) {
    const visualizationsToDownload = this.getSanitizedContent(filteredContent);

    const jsonData = JSON.stringify(visualizationsToDownload, undefined, 2);
    const blob = new Blob([jsonData], { type: 'text/json' });

    return window.URL.createObjectURL(blob);
  }

  // thank you stack overflow:
  // https://stackoverflow.com/questions/58830976/how-to-download-the-file-from-server-location-in-react-js
  onReportDownload(filteredContent) {
    const downloadUrl = this.genReportDownloadBlob(filteredContent);
    const a = window.document.createElement('a');
    window.document.body.appendChild(a);
    a.style.display = 'none';
    return function (fileName) {
      a.href = downloadUrl;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
    };
  }

  /**
   * Create a File Input and click it to initiate the file upload process
   */
  callImportReport() {
    this.hiddenFileInput.current.click();
  }

  /**
   * HTML Input File element change handler -- triggers when a file has been uploaded
   *
   * @param inputEl The Input HTML Element
   */
  onFileUploaded(inputEl) {
    const file = inputEl.files[0];
    const reader: any = new FileReader();
    let importNewReports: IVizExport[];

    reader.onload = () => {
      // when the reader loads, parse the results as JSON
      importNewReports = JSON.parse(reader.result);
      this.setState({
        newReports: importNewReports,
      });

      this.compareReports(
        this.getSanitizedContent(),
        importNewReports,
        this.props.datasets,
      );
    };
    reader.onerror = () => {
      console.error(reader.error);
    };
    // load the file into the reader
    reader.readAsText(file);
  }

  /**
   * Get the content which tracks state in a schema
   * that matches props.visualizations that does not
   * track anything
   */
  getSanitizedContent(
    filteredContent?: (IDehydratedViz & { description: string })[],
  ) {
    const { search, visualizations: allContent } = this.props;
    return sanitizeLibraryContent(allContent, filteredContent, search);
  }

  /**
   * Do a compare of two reports arrays to find duplicate names
   *
   * @param existingReports
   * @param newReports
   * @param datasets
   */
  compareReports(
    existingReports: any,
    newReports: IVizExport[],
    datasets: IDataset[],
  ) {
    const reportsRequiringInput = [];
    const reportsNotRequiringInput = [];

    _.forEach(newReports, (newRpt: IVizReportEditing) => {
      const existingReport = _.find(existingReports, {
        name: newRpt.name,
      });

      const needsNameReplacement = !_.isNil(existingReport);

      const updatingDataset = _.find(datasets, { name: newRpt.datasetName });

      if (needsNameReplacement) {
        const num = this.findNewReportNum(2, newRpt.name, existingReports);
        // set newName for future use
        newRpt.newName = `${newRpt.name} ${num}`;
        newRpt.needsName = true;
      }

      if (!_.isNil(updatingDataset)) {
        newRpt.datasetId = updatingDataset.id;
      }

      if (needsNameReplacement || _.isNil(updatingDataset)) {
        newRpt.needsName = needsNameReplacement;
        if (_.size(datasets) === 1) {
          // forces dataset and indicates to user that dataset has been set
          newRpt.datasetName = _.head(datasets)?.name;
          newRpt.datasetId = _.head(datasets)?.id;
        }
        newRpt.needsDataset = _.isNil(updatingDataset);
        reportsRequiringInput.push({
          newRpt,
          existingRpt: existingReport,
        });
      } else {
        reportsNotRequiringInput.push(newRpt);
      }
    });

    if (reportsRequiringInput.length) {
      this.setState({
        reportsRequiringInput,
        reportsNotRequiringInput,
        showImportDuplicates: true,
      });
    } else {
      const noDeletedReports: IVizReportEditing[] = [];
      this.doImportReportsSave(newReports, noDeletedReports);
    }
  }

  /**
   * Looks for a report name with a specific number after it,
   * if not found returns the num, otherwise advances num and searches again
   *
   * @param num The number to look for
   * @param rptName The report name
   * @param existingReports The existing reports array
   * @return {Number} The number to add to the report to make it unique
   */
  findNewReportNum(num, rptName, existingReports): number {
    const idx = _.findIndex(existingReports, { name: `${rptName} ${num}` });
    if (idx === -1) {
      // report not found with this name, return num
      return num;
    } else {
      // report name found, advance num and look again
      return this.findNewReportNum(++num, rptName, existingReports);
    }
  }

  /**
   * Handles closing the Import Reports modal
   */
  doImportReportsClose() {
    this.setState({
      reportsRequiringInput: undefined,
      newReports: undefined,
      showImportDuplicates: false,
    });
  }

  /**
   * Handles the save for importing reports
   */
  doImportReportsSave(
    reportsToImport: IVizReportEditing[],
    reportsToDelete: IVizReportEditing[],
  ) {
    const clean = n => {
      return _.pick(n, [
        'id',
        'name',
        'datasetId',
        'private',
        'chartType',
        'options',
        'layout',
      ]);
    };

    const importingReports: IVizImport[] = _.map(reportsToImport, rpt => {
      return clean(rpt);
    });

    _.forEach(importingReports, rpt => {
      this.props.newVisualization(rpt);
    });

    const deletingReports: IVizImport[] = _.map(reportsToDelete, rpt => {
      return clean(rpt);
    });

    _.forEach(deletingReports, rpt => {
      this.props.deleteVisualization(rpt.id);
    });

    this.doImportReportsClose();
  }

  render() {
    const {
      showDelete,
      showEdit,
      showImportDuplicates,
      editKey,
      updatingViz,
      reportsRequiringInput,
      reportsNotRequiringInput,
    } = this.state;
    const {
      visualizations,
      isAdvanced,
      isAdmin,
      currentUser,
      search,
      tags,
      chartTypes,
      searchDatasets,
      loading,
    } = this.props;
    const columns = isAdvanced
      ? TableSpec.columns
      : _.reject(TableSpec.columns, 'advanced');

    const filteredContent = _.filter<IDehydratedViz & { description: string }>(
      visualizations,
      viz =>
        (_.isEmpty(tags) || !_.isEmpty(_.intersection(viz.tags, tags))) &&
        (_.isEmpty(chartTypes) || _.includes(chartTypes, viz.chartType)) &&
        (_.isEmpty(searchDatasets) ||
          _.includes(searchDatasets, viz?.dataset?.name)),
    );

    return (
      <Root className='Library'>
        <MainSectionHeader
          headerLabel={messages.nav.home}
          actionButtons={[
            !this.props.isReadOnly && (
              <CreateReportButton key={'create-report'} />
            ),
          ].filter(Boolean)}
          advancedActions={
            <ButtonContainer>
              <SecondaryButton
                className='report-defs import-report'
                onClick={() => this.callImportReport()}
              >
                {messages.library.importReportButtonLabel}
              </SecondaryButton>
              <SecondaryButton
                className='report-defs export-report'
                onClick={() =>
                  this.onReportDownload(filteredContent)(
                    `export-${this.state.tenantName}-reports.json`,
                  )
                }
              >
                {messages.library.exportReportButtonLabel}
              </SecondaryButton>
            </ButtonContainer>
          }
        />
        <input
          type='file'
          ref={this.hiddenFileInput}
          onChange={event => this.onFileUploaded(event.target)}
          style={{ display: 'none' }}
          className={'importFileUpload'}
        />
        <TableToolBar className='library-table__toolbar'>
          <TextSearchField
            placeholder={messages.library.searchPlaceholder}
            value={search}
            onChange={this.onSearch.bind(this)}
            large
          />
          <FilterWidgetContextProvider>
            <SearchOptionGroup>
              <FilterWidget
                placeholder={messages.tags.searchPlaceholder}
                disableAdd={true}
                disableSelectAll={false}
                onSave={tags => {
                  this.onSetTags(tags);
                }}
                headerSx={{
                  marginRight: 0,
                  width: `${LIBRARY_FILTER_HEADER_WIDTH}px`,
                }}
              />
              <FilterWidget
                placeholder={messages.library.searchChartTypePlaceholder}
                disableAdd={true}
                disableSelectAll={false}
                onSave={t => this.onSetSearchChartType(t)}
                widgetType='chartTypes'
                headerSx={{
                  marginRight: 0,
                  width: `${LIBRARY_FILTER_HEADER_WIDTH}px`,
                }}
              />
              <FilterWidget
                placeholder={messages.library.searchDatasetsPlaceholder}
                disableAdd={true}
                disableSelectAll={false}
                onSave={t => this.onSetDatasets(t)}
                widgetType='datasets'
                headerSx={{
                  marginRight: 0,
                  width: `${LIBRARY_FILTER_HEADER_WIDTH}px`,
                }}
              />
            </SearchOptionGroup>
          </FilterWidgetContextProvider>
        </TableToolBar>
        <UITable
          className='library-table'
          domain='library'
          data={filteredContent}
          search={search}
          searchOptions={TableSpec.searchOptions}
          columns={columns}
          loading={loading}
        >
          {({ Column, HeaderRenderer, HeaderRendererWithSort }) => {
            return columns.map(
              col =>
                !col.hidden && (
                  <Column
                    style={{
                      height: '100%',
                      width: col.width,
                      flexGrow: col.flex ?? 1,
                      maxWidth: col.maxWidth,
                    }}
                    headerStyle={{
                      height: '100%',
                      width: col.width,
                      flexGrow: col.flex ?? 1,
                      maxWidth: col.maxWidth,
                    }}
                    key={col.key}
                    dataKey={col.key}
                    label={_.get(messages, col.label, col.label)}
                    headerRenderer={
                      col.sortable ? HeaderRendererWithSort : HeaderRenderer
                    }
                    disableSort={!col.sortable}
                    cellRenderer={({
                      rowData,
                    }: {
                      rowData: IDehydratedViz & { description: string };
                    }) => {
                      if (loading) {
                        return <TableSkeleton />;
                      }

                      return col.cellRenderer({
                        rowData,
                        currentUser,
                        isAdmin,
                        setActionsOpen: this.props.setActionsOpen,
                        doAction: this.doAction.bind(this),
                        isReadOnly: this.props.isReadOnly,
                        onClick: this.onRowClick.bind(this),
                      });
                    }}
                    cellDataGetter={defaultCellDataGetter}
                    width={col.width}
                    flexGrow={col.flex}
                    maxWidth={col.maxWidth}
                  />
                ),
            );
          }}
        </UITable>
        {showDelete && (
          <PromptDialog
            show={showDelete}
            title={messages.library.deleteReportTitle}
            detail={this.getDeleteConfirmation()}
            doYes={() => this.doDeleteYes()}
            doNo={() => this.doDeleteNo()}
          />
        )}
        {showImportDuplicates && (
          <ImportReportsDuplicateDialog
            show={showImportDuplicates}
            doSave={(newReports, deleteReports) =>
              this.doImportReportsSave(newReports, deleteReports)
            }
            doCancel={() => this.doImportReportsClose()}
            reportsRequiringInput={reportsRequiringInput}
            reportsNotRequiringInput={reportsNotRequiringInput}
            datasets={this.props.datasets}
          />
        )}
        {showEdit && (
          <EditDiscoveryDialog
            show={showEdit}
            detail={editKey}
            value={
              editKey === 'name' ? updatingViz?.name : updatingViz?.description
            }
            onOk={value => this.doEditYes(value)}
            onCancel={() => this.doEditNo()}
          />
        )}
      </Root>
    );
  }
}

export default withErrorBoundary(
  compose(
    withState('actionsOpen', 'setActionsOpen', false),
    ToastHOC,
  )(LibraryComponentUnconnected as any),
  ErrorBoundaryHOCOptions,
);
